
import * as React from 'react';
import {
  switchLayout as defaultSwitchLayout,
  updateAvailableLayouts as defaultUpdateAvailableLayouts,
  setDefaultLayout as defaultSetDefaultLayout
} from '../../operations/switchLayout';
import { layoutTypeFacet, availableLayoutsFacet, currentItemFacet } from '../../facets/ScopeFacets';
import { ILayoutState, LayoutManager, ILayoutManagerStateProps } from '../layoutManager/LayoutManager';
import { DetailsLayout, CompactDetailsLayout } from '../layouts/DetailsLayout';
import { TilesLayout } from '../layouts/TilesLayout';
import { LayoutSwitcher } from '../layoutSwitcher/LayoutSwitcher';
import { ItemsError } from '../itemsError/ItemsError';
import { ItemsEmptyFolder } from '../itemsEmptyFolder/ItemsEmptyFolder';
import { connectToItemCache } from '../decorators/connectToItemCache';
import { IItemCache } from '../../models/IItemCacheObserver';
import { connectToItemsScope } from '../decorators/connectToItemsScope';
import { DefaultDropZone, renderChildrenWithDropZoneElement } from '../defaultDropZone/DefaultDropZone';
import { itemsViewControl } from '../../controls/itemsViewControl';
import { LayoutTypeFacet } from '../../models/layouts/LayoutTypeFacet';
import { IComponentDecorator, IDecoratedProps } from '../../utilities/components/Decorators';
import { getCurrentItemSetKey } from '../decorators/selectors';
import { IReactChildrenProps } from '../../utilities/components/Props';
import { evaluateAction } from '../withAction/WithAction';

interface IBaseItemsViewProps {
  layouts: ILayoutManagerStateProps['layouts'];
  layoutType: LayoutTypeFacet | undefined;
  switchLayout: (payload: {
    layoutType: LayoutTypeFacet | undefined;
  }) => void;
  updateAvailableLayouts: (payload: {
    layouts: { [layoutId: string]: boolean; };
  }) => void;
  pinnedItemsView: JSX.Element | undefined;
  defaultLayout: LayoutTypeFacet | undefined;
  setDefaultLayout: ((payload: {
    layoutType: LayoutTypeFacet;
  }) => void);
  isLayoutResolved: boolean | undefined;
  shouldShowEmptyFolder?: (payload: { layoutKey: string | undefined }) => boolean;
  shouldShowPinnedItems?: (payload: { layoutKey: string | undefined }) => boolean;
  dragDropElement: JSX.Element | null;
}

interface IBaseItemsViewState {
  selectedLayoutType?: LayoutTypeFacet | undefined;
}

class BaseItemsView extends React.PureComponent<IBaseItemsViewProps, IBaseItemsViewState> {
  constructor(props: IBaseItemsViewProps) {
    super(props);

    const {
      layoutType,
      defaultLayout,
      isLayoutResolved,
      layouts
    } = this.props;

    const layoutKeys = [
      ...(layoutType ? [layoutType.id] : []),
      ...(defaultLayout ? [defaultLayout.id] : []),
      ...Object.keys(layouts)
    ];

    const firstAvailableLayout = layoutKeys.map((key: string) => layouts[key]).filter((layout: ILayoutState | undefined) => {
      return layout && layout.isAvailable;
    })[0];

    this.state = {
      selectedLayoutType: isLayoutResolved !== false ?
        firstAvailableLayout.facet :
        undefined
    };
  }

  public componentWillReceiveProps(nextProps: IBaseItemsViewProps): void {
    const {
      layoutType,
      defaultLayout,
      isLayoutResolved,
      layouts
    } = nextProps;

    const { props } = this;

    const layoutKeys = [
      ...(layoutType ? [layoutType.id] : []),
      ...(defaultLayout ? [defaultLayout.id] : []),
      ...Object.keys(layouts)
    ];

    const firstAvailableLayout = layoutKeys.map((key: string) => layouts[key]).filter((layout: ILayoutState | undefined) => {
      return layout && layout.isAvailable;
    })[0];

    if (
      // Only allow the layout to change if there is a valid layout to switch to.
      firstAvailableLayout &&
      // Only allow the layout to change if the layout is not explicitly unresolved.
      isLayoutResolved !== false &&
      (
        // Allow the layout to change if the user specifically changed it.
        (layoutType !== props.layoutType) ||
        // Otherwise, allow the layout to change only with certain updates to the 'default' layout.
        (
          (defaultLayout !== props.defaultLayout || !this.state.selectedLayoutType) &&
          // Only allow a change if the default layout did not suddenly clear.
          (defaultLayout || !props.defaultLayout) &&
          // Only allow a layout change when explicitly changing resolution state.
          isLayoutResolved !== props.isLayoutResolved
        )
      )
    ) {
      // The current layout, once rendered, should change as infrequently as possible to avoid disrupting the user.
      this.setState({
        selectedLayoutType: firstAvailableLayout.facet
      });
    }
  }

  public render(): JSX.Element | null {
    const {
      pinnedItemsView,
      shouldShowEmptyFolder,
      shouldShowPinnedItems,
      dragDropElement
    } = this.props;

    const {
      selectedLayoutType
    } = this.state;

    const layoutKey = selectedLayoutType && selectedLayoutType.id;

    const currentLayout = layoutKey && this.props.layouts[layoutKey];
    const component = currentLayout && currentLayout.content;

    const showEmptyFolder = shouldShowEmptyFolder ?
      shouldShowEmptyFolder({ layoutKey: layoutKey }) :
      true;

    const showPinnedItems = shouldShowPinnedItems ?
      shouldShowPinnedItems({ layoutKey: layoutKey }) :
      true;

    return renderChildrenWithDropZoneElement((
      <>
        { showPinnedItems && pinnedItemsView }
        <LayoutSwitcher key='layout' layoutKey={ layoutKey }>
          { component }
        </LayoutSwitcher>
        <ItemsError key='error' />
        { showEmptyFolder && (
          <ItemsEmptyFolder key='emptyFolder' />
        ) }
      </>
    ), dragDropElement);
  }

  public componentDidMount(): void {
    const {
      layouts,
      isLayoutResolved,
      setDefaultLayout
    } = this.props;

    const {
      selectedLayoutType
    } = this.state;

    if (selectedLayoutType &&
      isLayoutResolved !== false) {
      setDefaultLayout({
        layoutType: selectedLayoutType
      });
    }

    this._clearLayoutIfInactive(this.props);

    this._updateAvailableLayouts(layouts);
  }

  public componentDidUpdate(previousProps: IBaseItemsViewProps, previousState: IBaseItemsViewState): void {
    const {
      layouts,
      isLayoutResolved,
      setDefaultLayout
    } = this.props;

    const {
      selectedLayoutType
    } = this.state;

    if (selectedLayoutType &&
      (selectedLayoutType !== previousState.selectedLayoutType ||
        isLayoutResolved !== previousProps.isLayoutResolved) &&
      isLayoutResolved !== false) {
      setDefaultLayout({
        layoutType: selectedLayoutType
      });
    }

    this._clearLayoutIfInactive(this.props);

    if (previousProps.layouts !== layouts) {
      this._updateAvailableLayouts(layouts);
    }
  }

  private _clearLayoutIfInactive(props: IBaseItemsViewProps): void {
    const {
      layoutType,
      layouts,
      isLayoutResolved,
      switchLayout
    } = props;

    if (layoutType &&
      (!layouts[layoutType.id] || !layouts[layoutType.id].isAvailable) &&
      isLayoutResolved !== false) {
      switchLayout({
        layoutType: undefined
      });
    }
  }

  private _updateAvailableLayouts(layouts: ILayoutManagerStateProps['layouts']): void {
    const availableLayouts: typeof availableLayoutsFacet.type = {};

    for (const key in layouts) {
      if (Object.prototype.hasOwnProperty.call(layouts, key)) {
        const layoutState = layouts[key];

        if (layoutState) {
          availableLayouts[layoutState.facet.id] = layoutState.isAvailable;
        }
      }
    }

    this.props.updateAvailableLayouts({
      layouts: availableLayouts
    });
  }
}

interface IItemsViewOwnProps {
  itemsScopeItemKey: string;
}

interface IConfigurableLayoutsOwnProps extends IReactChildrenProps {
  additionalLayouts: JSX.Element[] | undefined;
}

function withConfigurableLayouts(): IComponentDecorator<ILayoutManagerStateProps, IConfigurableLayoutsOwnProps> {
  return <P extends ILayoutManagerStateProps>(
    component: React.ComponentType<P>
  ): React.ComponentType<IDecoratedProps<P, ILayoutManagerStateProps, IConfigurableLayoutsOwnProps>> => {
    const Component = component;

    type IProps = IDecoratedProps<P, ILayoutManagerStateProps, IConfigurableLayoutsOwnProps>;

    class LayoutConfigurator extends React.PureComponent<IProps> {
      public render(): JSX.Element {
        const {
          children,
          additionalLayouts,
          ...props
        } = this.props as IConfigurableLayoutsOwnProps;

        function renderWithLayouts({ layouts }: ILayoutManagerStateProps): JSX.Element {
          return (
            <Component layouts={ layouts } { ...props as P } />
          );
        }

        return (
          <LayoutManager
            key='manager'
            withLayouts={
              renderWithLayouts
            }
          >
            {
              // All children are assumed to be layout configurations.
            }
            { children }
            <LayoutManager>
              <DetailsLayout key='detailsLayout' />
              <CompactDetailsLayout key='compactDetailsLayout' />
              <TilesLayout key='tilesLayout' />
              { additionalLayouts }
            </LayoutManager>
          </LayoutManager>
        );
      }
    }

    return LayoutConfigurator;
  };
}

/**
 * @public
 */
export interface IItemsViewProps {
  // Nothing.
}

/**
 * @public
 */
export const ItemsView: React.ComponentType<IItemsViewProps> = connectToItemsScope()(
  connectToItemCache((itemCache: IItemCache, props: IItemsViewOwnProps):
    Pick<IBaseItemsViewProps,
    'layoutType' | 'switchLayout' | 'updateAvailableLayouts' |
    'pinnedItemsView' | 'defaultLayout' |
    'setDefaultLayout' | 'isLayoutResolved' | 'shouldShowEmptyFolder' | 'shouldShowPinnedItems' |
    'dragDropElement'
    > & Pick<IConfigurableLayoutsOwnProps, 'additionalLayouts'> => {
    const {
      itemsScopeItemKey
    } = props;

    const { dispatch } = itemCache;

    const {
      layoutType
    } = itemCache.demandItem({
      currentItem: currentItemFacet,
      layoutType: layoutTypeFacet
    }, itemsScopeItemKey);

    const itemSetKey = getCurrentItemSetKey(itemCache);

    const controlHandler = itemCache.demandItemFacet(itemsViewControl, itemSetKey);
    const {
      defaultLayout,
      pinnedItemsView,
      additionalLayouts,
      setDefaultLayout,
      isLayoutResolved,
      shouldShowEmptyFolder,
      shouldShowPinnedItems
    } = itemsViewControl.evaluate(controlHandler)(itemCache, {
      itemsScopeItemKey: itemsScopeItemKey,
      itemKey: itemSetKey,
      setDefaultLayout: defaultSetDefaultLayout,
      isLayoutResolved: !itemSetKey ? false : undefined,
      pinnedItemsView: undefined,
      additionalLayouts: undefined
    });

    const boundUpdateAvailableLayouts = (payload: {
      layouts: typeof availableLayoutsFacet.type;
    }) => {
      dispatch(defaultUpdateAvailableLayouts({
        itemsScopeItemKey: itemsScopeItemKey,
        layouts: payload.layouts
      }));
    };

    const boundSetDefaultLayout = (payload: {
      layoutType: LayoutTypeFacet
    }) => {
      dispatch(setDefaultLayout({
        layoutType: payload.layoutType,
        itemsScopeItemKey
      }));
    };

    const boundSwitchLayout = (payload: {
      layoutType: LayoutTypeFacet | undefined;
    }) => {
      dispatch(defaultSwitchLayout({
        itemsScopeItemKey,
        layoutType: payload.layoutType
      }));
    };

    const dragDropElement = evaluateAction(itemCache, (
      <DefaultDropZone />
    ));

    return {
      layoutType,
      switchLayout: boundSwitchLayout,
      updateAvailableLayouts: boundUpdateAvailableLayouts,
      pinnedItemsView: pinnedItemsView,
      additionalLayouts: additionalLayouts,
      defaultLayout: defaultLayout,
      setDefaultLayout: boundSetDefaultLayout,
      isLayoutResolved,
      shouldShowEmptyFolder,
      shouldShowPinnedItems,
      dragDropElement
    };
  })(withConfigurableLayouts()(BaseItemsView))
);
